import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { useLanguage } from '../../../contexts/LanguageContext';

const BulkOperationsPanel = ({ selectedCount, onBulkStatusUpdate, onBulkDocumentRequest, onBulkNotification }) => {
  const { t } = useLanguage();
  const [selectedAction, setSelectedAction] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');

  const actionOptions = [
    { value: '', label: t('visas.selectAction') },
    { value: 'update_status', label: t('visas.updateStatus') },
    { value: 'request_documents', label: t('visas.requestDocuments') },
    { value: 'send_notification', label: t('visas.sendNotification') }
  ];

  const statusOptions = [
    { value: 'pending', label: t('common.pending') },
    { value: 'in_review', label: t('visas.underReview') },
    { value: 'approved', label: t('visas.approved') },
    { value: 'rejected', label: t('visas.rejected') }
  ];

  const templateOptions = [
    { value: 'visa_approved', label: t('visas.visaApprovedTemplate') },
    { value: 'documents_required', label: t('visas.documentsRequiredTemplate') },
    { value: 'processing_update', label: t('visas.processingUpdateTemplate') },
    { value: 'expiration_reminder', label: t('visas.expirationReminderTemplate') }
  ];

  const handleExecute = () => {
    if (selectedAction === 'update_status' && selectedStatus) {
      onBulkStatusUpdate(selectedStatus);
      setSelectedAction('');
      setSelectedStatus('');
    } else if (selectedAction === 'request_documents') {
      onBulkDocumentRequest();
      setSelectedAction('');
    } else if (selectedAction === 'send_notification' && selectedTemplate) {
      onBulkNotification(selectedTemplate);
      setSelectedAction('');
      setSelectedTemplate('');
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-3 sm:p-4 md:p-6 shadow-warm">
      <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
          <Icon name="Layers" size={20} className="text-accent" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-base sm:text-lg font-semibold text-foreground truncate">{t('visas.bulkOperationsTitle')}</h3>
          <p className="caption text-muted-foreground truncate">
            {selectedCount} {selectedCount !== 1 ? t('visas.requestsPlural') : t('visas.requestsSingular')} {t('visas.selected')}
          </p>
        </div>
      </div>

      {selectedCount > 0 ? (
        <div className="space-y-4">
          <Select
            label={t('visas.actions')}
            options={actionOptions}
            value={selectedAction}
            onChange={setSelectedAction}
          />

          {selectedAction === 'update_status' && (
            <Select
              label={t('visas.newStatus')}
              options={statusOptions}
              value={selectedStatus}
              onChange={setSelectedStatus}
            />
          )}

          {selectedAction === 'send_notification' && (
            <Select
              label={t('visas.notificationTemplate')}
              options={templateOptions}
              value={selectedTemplate}
              onChange={setSelectedTemplate}
            />
          )}

          <Button
            variant="default"
            fullWidth
            iconName="Play"
            iconPosition="left"
            onClick={handleExecute}
            disabled={!selectedAction || (selectedAction === 'update_status' && !selectedStatus) || (selectedAction === 'send_notification' && !selectedTemplate)}
          >
            {t('visas.executeAction')}
          </Button>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 pt-4 border-t border-border">
            <Button
              variant="outline"
              size="sm"
              iconName="FileText"
              iconPosition="left"
              onClick={onBulkDocumentRequest}
              className="w-full"
            >
              <span className="hidden sm:inline">{t('visas.requestDocsShort')}</span>
              <span className="sm:hidden text-xs">{t('visas.requestDocsShort')}</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="Mail"
              iconPosition="left"
              onClick={() => onBulkNotification('general')}
              className="w-full"
            >
              <span className="hidden sm:inline">{t('visas.sendEmail')}</span>
              <span className="sm:hidden text-xs">{t('visas.sendEmail')}</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="MessageSquare"
              iconPosition="left"
              onClick={() => onBulkNotification('whatsapp')}
              className="w-full"
            >
              <span className="hidden sm:inline">{t('visas.sendWhatsApp')}</span>
              <span className="sm:hidden text-xs">WhatsApp</span>
            </Button>
          </div>
        </div>
      ) : (
        <div className="text-center py-6 sm:py-8">
          <Icon name="MousePointerClick" size={48} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground text-sm sm:text-base">{t('visas.selectRequestsPrompt')}</p>
        </div>
      )}
    </div>
  );
};

export default BulkOperationsPanel;